import { parseSocketEnvelope } from "./socket-protocol"
import type { ClientEvent, SocketEnvelope } from "./socket-protocol"
export type SocketStatus = "closed" | "connecting" | "open" | "reconnecting" | "failed"
export type SocketLike = { readyState: number; send(data: string): void; close(code?: number): void;
  onopen: (() => void) | null; onmessage: ((e: { data: unknown }) => void) | null;
  onclose: ((e: { code: number; reason: string }) => void) | null; onerror: (() => void) | null }
export type SocketDeps = {
  url: string
  createSocket(url: string, init: { headers?: Record<string, string> }): SocketLike
  setTimeout(fn: () => void, ms: number): unknown
  clearTimeout(handle: unknown): void
  now(): number
  getAuth(): Promise<{ headers?: Record<string, string> }>
  refreshSession(): Promise<"refreshed" | "transient_failure" | "definitive_failure">
  checkSession(): Promise<"valid" | "unauthorized" | "unknown">
  onEnvelope(envelope: SocketEnvelope): void
  onStatus(status: SocketStatus): void
  onSessionExpired(): void
  log(event: string, data?: Record<string, unknown>): void
}
type SubmitInput = { conversationId: string; content: string; clientMessageId: string; retryOfTurnId?: string; sourceFailedTurnId?: string }
type Pending = SubmitInput & { serverTurnId?: string; sentOn?: number }
const SESSION_EXPIRED = "Your session has expired. Please sign in again."

export function createSocketManager(deps: SocketDeps) {
  let socket: SocketLike | null = null
  let status: SocketStatus = "closed"
  let stopped = true
  let paused = false
  let connecting = false
  let generation = 0
  let attempt = 0
  let failedHandshakes = 0
  let refreshNeeded = false
  let warningsSuppressedUntil = 0
  let retryTimer: unknown = null
  let failureTimer: unknown = null
  let warningTimer: unknown = null
  let serial = 0
  const submits = new Map<string, Pending>()
  const attachments = new Map<string, number | undefined>()
  const settling = new Set<string>()
  const cancels = new Map<string, { serverTurnId?: string; sentOn?: number }>()
  const waiters = new Set<{ resolve(): void; reject(error: Error): void; timer: unknown }>()
  const setStatus = (next: SocketStatus) => { if (status !== next) { status = next; deps.onStatus(next) } }
  function clearTimers() {
    for (const timer of [retryTimer, failureTimer, warningTimer]) if (timer !== null) deps.clearTimeout(timer)
    retryTimer = failureTimer = warningTimer = null
  }
  function finishWaiters(error?: Error) {
    for (const waiter of waiters) {
      deps.clearTimeout(waiter.timer)
      if (error) waiter.reject(error); else waiter.resolve()
    }
    waiters.clear()
  }
  function expire() {
    stopped = true
    connecting = false
    generation += 1
    clearTimers()
    const old = socket
    socket = null
    old?.close()
    submits.clear(); attachments.clear(); cancels.clear(); settling.clear()
    finishWaiters(new Error(SESSION_EXPIRED))
    setStatus("closed")
    deps.onSessionExpired()
  }
  function send(event: ClientEvent, payload: Record<string, unknown>) {
    if (stopped || paused || socket?.readyState !== 1) return false
    try {
      socket.send(JSON.stringify({ event, event_id: `${deps.now().toString(36)}-${generation}-${++serial}`, ts: new Date(deps.now()).toISOString(), payload }))
      deps.log("send", { event, payloadKeys: Object.keys(payload).sort(), payload })
      return true
    } catch {
      deps.log("send failed", { event })
      socket.close()
      return false
    }
  }
  function flush() {
    for (const pending of submits.values()) {
      if (pending.serverTurnId) attachments.set(pending.serverTurnId, attachments.get(pending.serverTurnId))
      else if (pending.sentOn !== generation && send("message.submit", {
        conversation_id: pending.conversationId, content: pending.content,
        client_message_id: pending.clientMessageId, idempotency_key: pending.clientMessageId,
        retry_of_turn_id: pending.retryOfTurnId, source_failed_turn_id: pending.sourceFailedTurnId,
      })) pending.sentOn = generation
    }
    for (const [turnId, sentOn] of attachments) {
      if (sentOn !== generation && send("turn.attach", { turn_id: turnId })) attachments.set(turnId, generation)
    }
    for (const cancel of cancels.values()) {
      if (cancel.serverTurnId && cancel.sentOn !== generation && send("message.cancel", { turn_id: cancel.serverTurnId })) cancel.sentOn = generation
    }
  }
  function failConnection() {
    // Abandon failed submissions, retaining durable cancel intent and committed
    // answers waiting for their final settlement, as the existing web did.
    for (const [id, pending] of submits) {
      if (!cancels.has(pending.conversationId) && !settling.has(pending.serverTurnId ?? "")) submits.delete(id)
    }
    for (const id of attachments.keys()) {
      if (!settling.has(id) && ![...cancels.values()].some(c => c.serverTurnId === id)) attachments.delete(id)
    }
    setStatus("failed")
  }
  function promoteWarning() {
    if (paused || stopped || socket?.readyState === 1) return
    const remaining = warningsSuppressedUntil - deps.now()
    if (remaining > 0) {
      if (warningTimer === null) warningTimer = deps.setTimeout(() => { warningTimer = null; promoteWarning() }, remaining)
      return
    }
    if (attempt > 0) setStatus("reconnecting")
    if (failureTimer === null) failureTimer = deps.setTimeout(() => {
      failureTimer = null
      if (!stopped && !paused && socket?.readyState !== 1) failConnection()
    }, 45_000)
  }
  function scheduleReconnect() {
    if (stopped || paused) return
    if (retryTimer !== null) deps.clearTimeout(retryTimer)
    if (attempt >= 10) {
      clearTimers()
      if (deps.now() < warningsSuppressedUntil) { setStatus("connecting"); promoteWarning() }
      else failConnection()
      return
    }
    if (deps.now() < warningsSuppressedUntil) setStatus("connecting")
    else setStatus("reconnecting")
    promoteWarning()
    const delay = Math.min(30_000, 1_000 * 2 ** attempt) + Math.floor(Math.random() * 1_000)
    attempt += 1
    deps.log("reconnect scheduled", { attempt, delayMs: delay })
    retryTimer = deps.setTimeout(() => { retryTimer = null; void openSocket() }, delay)
  }
  async function refresh(ticket: number) {
    let result: Awaited<ReturnType<SocketDeps["refreshSession"]>>
    try { result = await deps.refreshSession() } catch { result = "transient_failure" }
    if (ticket !== generation || stopped || paused) return false
    if (result === "definitive_failure") { expire(); return false }
    refreshNeeded = result !== "refreshed"
    return !refreshNeeded
  }
  function observe(envelope: SocketEnvelope) {
    const payload = envelope.payload
    const turnId = typeof payload.turn_id === "string" ? payload.turn_id : undefined
    const conversationId = typeof payload.conversation_id === "string" ? payload.conversation_id : undefined
    const pending = [...submits.values()].find(s => turnId && s.serverTurnId === turnId)
      ?? [...submits.values()].find(s => conversationId && s.conversationId === conversationId)
    if (turnId && (envelope.event === "turn.accepted" || envelope.event === "turn.attached")) {
      if (pending) { pending.serverTurnId = turnId; attachments.set(turnId, generation) }
      const cancel = conversationId ? cancels.get(conversationId) : undefined
      if (cancel) {
        cancel.serverTurnId = turnId
        if (envelope.event === "turn.attached") cancel.sentOn = undefined
        const resume = payload.resume as Record<string, unknown> | undefined
        if (payload.status === "completed" || payload.status === "failed" || payload.status === "settling" || resume?.phase === "settling") cancels.delete(conversationId!)
      }
    }
    if (turnId && (envelope.event === "turn.settling" || (envelope.event === "turn.attached" && (payload.resume as Record<string, unknown> | undefined)?.phase === "settling"))) settling.add(turnId)
    if (envelope.event === "turn.settling" && conversationId) cancels.delete(conversationId)
    if (envelope.event === "answer.final" || envelope.event === "turn.error") {
      if (pending) submits.delete(pending.clientMessageId)
      if (turnId) { attachments.delete(turnId); settling.delete(turnId) }
      if (conversationId) cancels.delete(conversationId)
      if (!turnId && !conversationId && submits.size === 1) {
        const only = [...submits.values()][0]!
        submits.delete(only.clientMessageId); cancels.delete(only.conversationId)
      }
    }
    deps.onEnvelope(envelope)
    // Cancel before acknowledgement can only be sent once the server ID arrives.
    if (envelope.event === "turn.accepted" || envelope.event === "turn.attached") flush()
  }
  async function openSocket() {
    if (stopped || paused || connecting || socket?.readyState === 0 || socket?.readyState === 1) return
    if (retryTimer !== null) { deps.clearTimeout(retryTimer); retryTimer = null }
    connecting = true
    const ticket = ++generation
    setStatus(attempt > 0 && deps.now() >= warningsSuppressedUntil ? "reconnecting" : "connecting")
    promoteWarning()
    try {
      if (refreshNeeded && !await refresh(ticket)) {
        if (ticket === generation && !stopped) { connecting = false; scheduleReconnect() }
        return
      }
      const auth = await deps.getAuth()
      if (ticket !== generation || stopped || paused) return
      deps.log("connect attempt", { reconnectAttempt: attempt })
      const current = deps.createSocket(deps.url, auth)
      socket = current
      connecting = false
      let opened = false
      current.onopen = () => {
        if (ticket !== generation || current !== socket || stopped) { current.close(); return }
        opened = true; failedHandshakes = 0; attempt = 0
        clearTimers(); warningsSuppressedUntil = 0
        deps.log("open")
        setStatus("open"); finishWaiters(); flush()
      }
      current.onmessage = e => {
        if (ticket !== generation || current !== socket || stopped) return
        const envelope = parseSocketEnvelope(e.data)
        if (envelope) observe(envelope)
      }
      current.onclose = e => {
        if (ticket !== generation || current !== socket) return
        socket = null
        deps.log("close", { code: e.code, reason: e.reason })
        if (stopped) return
        if (e.code === 1003) {
          // A rejected payload must not be replayed automatically.
          stopped = true; connecting = false; generation += 1; clearTimers()
          submits.clear(); attachments.clear(); cancels.clear(); settling.clear()
          finishWaiters(new Error("The server rejected the chat payload."))
          setStatus("failed")
          return
        }
        if (paused) return
        void (async () => {
          if (e.code === 4401) refreshNeeded = true
          else if (!opened && e.code === 1006) {
            failedHandshakes += 1
            if (failedHandshakes >= 2) {
              let result: Awaited<ReturnType<SocketDeps["checkSession"]>> = "unknown"
              try { result = await deps.checkSession() } catch { /* connectivity is not logout */ }
              if (ticket !== generation || stopped || paused) return
              if (result === "unauthorized") refreshNeeded = true
            }
          }
          if (refreshNeeded) {
            connecting = true
            await refresh(ticket)
            if (ticket !== generation || stopped || paused) return
            connecting = false
          }
          if (ticket === generation && !stopped && !paused) scheduleReconnect()
        })()
      }
      current.onerror = () => {
        if (ticket !== generation || current !== socket) return
        deps.log("error", { readyState: current.readyState })
        if (current.readyState !== 1) current.close()
      }
    } catch {
      if (ticket !== generation || stopped || paused) return
      connecting = false
      deps.log("connect failed")
      scheduleReconnect()
    }
  }
  function connect() { stopped = false; if (!paused) void openSocket() }
  function disconnect() {
    stopped = true; paused = false; connecting = false; generation += 1; attempt = 0; failedHandshakes = 0; refreshNeeded = false
    clearTimers(); warningsSuppressedUntil = 0
    const current = socket; socket = null; current?.close()
    submits.clear(); attachments.clear(); cancels.clear(); settling.clear()
    finishWaiters(new Error("The chat connection was disconnected.")); setStatus("closed")
  }
  function pauseForBackground() {
    paused = true; clearTimers(); warningsSuppressedUntil = 0
    if (connecting && !socket) { generation += 1; connecting = false }
    if (status === "reconnecting" || status === "failed") setStatus("connecting")
    deps.log("background pause")
  }
  function resumeFromBackground() {
    paused = false; attempt = 0; warningsSuppressedUntil = deps.now() + 10_000
    clearTimers(); deps.log("background resume")
    if (stopped) return
    if (socket?.readyState === 1) { flush(); return }
    if (socket?.readyState === 0) { setStatus("connecting"); promoteWarning(); return }
    void openSocket()
  }
  function waitForOpen(timeoutMs = 8_000): Promise<void> {
    if (!paused && socket?.readyState === 1) return Promise.resolve()
    if (stopped) return Promise.reject(new Error("The chat connection was disconnected."))
    return new Promise((resolve, reject) => {
      const waiter = { resolve, reject, timer: null as unknown }
      waiter.timer = deps.setTimeout(() => { waiters.delete(waiter); reject(new Error("Timed out waiting for the chat connection to open.")) }, timeoutMs)
      waiters.add(waiter)
    })
  }
  async function submit(input: SubmitInput) {
    if (!submits.has(input.clientMessageId)) submits.set(input.clientMessageId, { ...input })
    connect(); flush()
    try { await waitForOpen() }
    catch (error) { submits.delete(input.clientMessageId); throw error }
  }
  function cancel(conversationId: string, serverTurnId?: string) {
    const current = cancels.get(conversationId)
    cancels.set(conversationId, { serverTurnId: serverTurnId ?? current?.serverTurnId, sentOn: current?.sentOn })
    connect(); flush()
  }
  function attach(serverTurnId: string) {
    if (!attachments.has(serverTurnId)) attachments.set(serverTurnId, undefined)
    connect(); flush()
  }
  return { connect, disconnect, pauseForBackground, resumeFromBackground, send, submit, cancel, attach, waitForOpen }
}
